/**
 * Safety net for donations where the browser never confirmed the grant with the
 * backend (closed tab, killed app, dropped network) before PayPal already charged
 * the user. We persist the txn to localStorage (survives closed tabs, unlike
 * sessionStorage) and retry the grant call once the app is reopened.
 */
const PENDING_DONATION_KEY = 'pending_donation_v1';
const MAX_ATTEMPTS = 5;
const MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000;

function readPending() {
    if (typeof window === 'undefined') return null;
    try {
        const raw = localStorage.getItem(PENDING_DONATION_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (_) {
        return null;
    }
}

function writePending(record) {
    try {
        localStorage.setItem(PENDING_DONATION_KEY, JSON.stringify(record));
    } catch (_) {
        /* ignore */
    }
}

export function savePendingDonation({ txnId, custom }) {
    if (typeof window === 'undefined' || !txnId) return;
    writePending({
        txn_id: txnId,
        custom: custom || '',
        attempts: 0,
        savedAt: Date.now(),
    });
}

export function clearPendingDonation() {
    if (typeof window === 'undefined') return;
    try {
        localStorage.removeItem(PENDING_DONATION_KEY);
    } catch (_) {
        /* ignore */
    }
}

function usernameFromToken(token) {
    if (!token) return null;
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.username || null;
    } catch {
        return null;
    }
}

/**
 * Best-effort, silent retry of a donation that never confirmed. Safe to call on
 * every app load: no-ops if there is nothing pending, and the backend grant is
 * idempotent (re-confirming an already-credited txn just refreshes tokens).
 */
export async function retryPendingDonationIfAny(getHost) {
    if (typeof window === 'undefined') return false;
    const pending = readPending();
    if (!pending || !pending.txn_id) return false;

    const tooOld = Date.now() - (pending.savedAt || 0) > MAX_AGE_MS;
    const tooManyAttempts = (pending.attempts || 0) >= MAX_ATTEMPTS;
    if (tooOld || tooManyAttempts) {
        clearPendingDonation();
        return false;
    }

    const token = localStorage.getItem('authToken');
    if (!token) return false; // not logged in yet; try again on a future load

    let custom = pending.custom || '';
    if (!custom) {
        const username = usernameFromToken(token);
        if (username) custom = `username:${username}`;
    }

    try {
        const res = await fetch(`${getHost()}/api/donation-paypal-complete`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ txn_id: pending.txn_id, custom }),
        });
        const body = await res.json().catch(() => ({}));
        if (res.ok) {
            if (body.token) localStorage.setItem('authToken', body.token);
            if (body.refresh_token) localStorage.setItem('refreshToken', body.refresh_token);
            clearPendingDonation();
            return true;
        }
        writePending({ ...pending, attempts: (pending.attempts || 0) + 1 });
        return false;
    } catch (_) {
        writePending({ ...pending, attempts: (pending.attempts || 0) + 1 });
        return false;
    }
}